import { useCallback, useState } from 'react';
import Layout from '@/layouts/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heading } from '@/components/ui/heading';
import { CircularProgress } from '@/components/ui/circular-progress';
import { router } from '@inertiajs/react';
import { Calendar, BookOpen, TrendingUp, Clock } from 'lucide-react';
import JournalEntryList from '@/components/journal/JournalEntryList';
import type { JournalEntry } from '@domain/journal';

type Props = {
  journals: JournalEntry[];
  today_entry: JournalEntry | null;
  today_date: string;
};

type JournalStatus = 'completed' | 'in_progress' | 'not_started';

const getStatusText = (status: JournalStatus) => {
  switch (status) {
    case 'completed':
      return 'Completato';
    case 'in_progress':
      return 'In corso';
    default:
      return 'Non avviato';
  }
};

const getStatusVariant = (status: JournalStatus): 'default' | 'secondary' | 'outline' => {
  switch (status) {
    case 'completed':
      return 'default';
    case 'in_progress':
      return 'secondary';
    default:
      return 'outline';
  }
};


export default function Index({ journals, today_entry, today_date }: Props) {
  const [loading, setLoading] = useState(false);

  const handleStartJournal = useCallback(() => {
    setLoading(true);
    router.post(
      '/patients/journals/start',
      {},
      {
        preserveScroll: true,
        onFinish: () => setLoading(false),
        onError: () => setLoading(false),
      },
    );
  }, []);

  const todayStatus: JournalStatus = (() => {
    if (today_entry?.completedAt || today_entry?.currentStep === 'completed') return 'completed';
    if (today_entry?.currentStep) return 'in_progress';
    return 'not_started';
  })();
  const isCompletedToday = todayStatus === 'completed';
  const isInProgressToday = todayStatus === 'in_progress';

  const todayFormatted = new Date(today_date).toLocaleDateString('it-IT', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const todayProgress = today_entry?.progress?.completionPercentage ?? 0;
  const progressLabel = today_entry?.progress?.label ?? 'Completamento';
  const showProgressRing = todayStatus !== 'not_started';

  const weeklyStart = new Date();
  weeklyStart.setDate(weeklyStart.getDate() - 6);
  const weeklyCompletedFromHistory = journals.filter((entry) => {
    if (!entry.completedAt && entry.currentStep !== 'completed') return false;
    return new Date(entry.date) >= weeklyStart;
  }).length;
  const weeklyCompleted = weeklyCompletedFromHistory + (isCompletedToday ? 1 : 0);

  return (
    <Layout user={{ name: 'Cliente' }}>
      <div className="w-full">
        <div className="mb-6">
          <Heading level={1} icon={BookOpen}>
            Il Mio Diario
          </Heading>
        </div>

        {/* Today's Journal Status Card */}
        <Card className="p-6 mb-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="flex-1">
              <Heading level={2} icon={Calendar} className="mb-2">
                Diario di oggi
              </Heading>
              <p className="text-muted-foreground mb-3">{todayFormatted}</p>

              <div className="flex flex-wrap items-center gap-3 mb-3">
                <Badge variant={getStatusVariant(todayStatus)}>{getStatusText(todayStatus)}</Badge>
                {isInProgressToday && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>Riprendi dove hai lasciato</span>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <Button
                  onClick={() => {
                    if (today_entry?.id && todayStatus !== 'not_started') {
                      router.get(`/patients/journals/${today_entry.id}`);
                      return;
                    }
                    handleStartJournal();
                  }}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  {loading
                    ? 'Avvio in corso...'
                    : isCompletedToday
                      ? 'Rivedi il diario'
                      : isInProgressToday
                        ? 'Riprendi il diario'
                        : 'Inizia il diario'}
                </Button>
              </div>
            </div>

            <div className="flex flex-col items-end gap-4">
              {showProgressRing && (
                <CircularProgress value={todayProgress} label={progressLabel} />
              )}
              <div className="w-full md:w-auto">
                <div className="text-muted-foreground mb-2">Questa settimana</div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <strong>{weeklyCompleted}/7 giorni</strong>
                </div>
                <div className="mt-2 h-2 w-40 rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary"
                    style={{ width: `${Math.min((weeklyCompleted / 7) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Journal History */}
        <div>
          <Heading level={2} className="mb-4">
            Diari completati
          </Heading>
          <JournalEntryList entries={journals} />
        </div>
      </div>
    </Layout>
  );
}
